import {
  type FontSize,
  type HeadingSize,
  type ThemeFlexDirection,
  type ThemeTextColor,
  fontSizeMap,
  themeTextColorMap,
} from '@mntn-dev/ui-theme'
import { cn } from '@mntn-dev/ui-utilities'
import { AnimatePresence, easeInOut, motion } from 'motion/react'
import type { TestIds } from '../../utils'
import { Heading } from '../heading'
import { LoadingSpinner } from '../loading'
import { Stack } from '../stack'
import { Text } from '../text'
import { CurrencyLabel } from './currency-label.tsx'

type CurrencyCreditContainerProps = Readonly<
  {
    currency?: number | string
    currencyUnitLabel: string
    label?: {
      text: string
      textColor?: ThemeTextColor
      fontSize?: FontSize
    }
    numeric?: {
      fontSize?: HeadingSize
      textColor?: ThemeTextColor
    }
    symbol?: {
      fontSize?: FontSize
    }
    direction?: ThemeFlexDirection
  } & TestIds
>

export const CurrencyCreditContainer = ({
  currency,
  currencyUnitLabel,
  label,
  numeric = {
    fontSize: '5xl',
  },
  symbol = {
    fontSize: '3xl',
  },
  direction = 'col',
  dataTestId,
  dataTrackingId,
}: CurrencyCreditContainerProps) => {
  if (currency === undefined || Number.isNaN(currency)) {
    return (
      <div className="w-full flex justify-end items-center">
        <LoadingSpinner className={cn('w-6 h-6', themeTextColorMap.info)} />
      </div>
    )
  }

  const formattedCurrency = currency?.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })

  return (
    <Stack direction={direction} gap="1" justifyContent="end" width="fit">
      <Stack alignItems="start" gap="1" paddingTop="1" justifyContent="end">
        {label && (
          <CurrencyLabel
            text={label.text}
            fontSize={label.fontSize}
            textColor={label.textColor}
          />
        )}

        <AnimatePresence>
          {currencyUnitLabel === '$' && (
            <motion.div
              initial={{ opacity: 0, width: 0, overflow: 'hidden' }}
              animate={{ opacity: 1, width: 'fit-content' }}
              exit={{ opacity: 0, width: 0, overflow: 'hidden' }}
              transition={{ duration: 0.3, ease: easeInOut }}
              className="w-full origin-top"
            >
              <span
                className={`text-primary-green drop-shadow-glow-green ${fontSizeMap[symbol.fontSize ?? '3xl']} font-bold leading-none`}
              >
                $
              </span>
            </motion.div>
          )}
        </AnimatePresence>

        <Heading
          fontSize={numeric.fontSize ?? '5xl'}
          className="font-bold leading-none"
          textColor={numeric?.textColor}
          dataTestId={dataTestId}
          dataTrackingId={dataTrackingId}
        >
          {formattedCurrency}
        </Heading>
      </Stack>
      <AnimatePresence>
        {currencyUnitLabel !== '$' && (
          <motion.div
            initial={{ opacity: 0, y: -16, height: 0, overflow: 'hidden' }}
            animate={{ opacity: 1, y: 0, height: 'fit-content' }}
            exit={{ opacity: 0, y: -16, height: 0, overflow: 'hidden' }}
            transition={{ duration: 0.3, ease: easeInOut }}
            className="flex w-full origin-top uppercase text-right self-end"
          >
            <Text fontSize="base" textColor="info">
              {currencyUnitLabel}
            </Text>
          </motion.div>
        )}
      </AnimatePresence>
    </Stack>
  )
}
